/**
 * Rules and reads over a worker's scheduled changes to budgeted hours per day.
 *
 * The engagement service owns the list: it sets the applied and cancelled stamps, cancels a pending entry a
 * save leaves out, and validates only the entries a save adds or edits. These mirror its rules so the
 * worker form refuses what the save would, worded the same way.
 *
 * Dates are the engagement's own yyyy-MM-dd strings throughout, which compare correctly as strings.
 */
import type { ScheduledBudgetedHoursEntryFormData as Entry } from '@/lib/schemas'

export const MAX_BUDGETED_HOURS_PER_DAY = 24

/** A local calendar date as the engagement stores it. */
export const toDateOnly = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Today on the Eastern business calendar, which is the day the service applies changes against. The
 * browser's own timezone would put the boundary an hour or more off around midnight outside Eastern time.
 */
export const todayDateOnly = (): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

/** The day after a yyyy-MM-dd date, in the same form. */
export const dayAfter = (date: string): string => {
  const [year, month, day] = date.split('-').map(Number)
  return toDateOnly(new Date(year, month - 1, day + 1))
}

/** Neither applied nor cancelled, so still waiting for its effective date. */
export const isPending = (entry: Entry): boolean => !entry.appliedDate && !entry.cancelledDate

/** The changes still to come, earliest first. Applied and cancelled ones live in audit history only. */
export const pendingEntries = (entries: Entry[] | null | undefined): Entry[] =>
  (entries ?? []).filter(isPending).sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate))

/**
 * The day the current hours were applied, from the latest applied change. Null when there is none, or when
 * its hours differ from the current ones, as after a direct edit that recorded no change.
 */
export const currentHoursAppliedDate = (entries: Entry[] | null | undefined, currentHours: number | null): string | null => {
  let latest: Entry | null = null
  for (const entry of entries ?? []) {
    // Later in the list wins a tie, since entries are only ever appended.
    if (entry.appliedDate && !entry.cancelledDate && (!latest || entry.appliedDate >= latest.appliedDate!)) {
      latest = entry
    }
  }
  return latest && latest.hoursPerDay === currentHours ? latest.appliedDate! : null
}

/** The last day the worker is on the engagement: their own, else the engagement's end. Null when open-ended. */
export const effectiveLastDay = (
  lastDayOfWork: Date | null | undefined,
  engagementEndDate: Date | null | undefined
): string | null => {
  const lastDay = lastDayOfWork ?? engagementEndDate
  return lastDay ? toDateOnly(lastDay) : null
}

/** A pending change dated after the worker's last day, which the service leaves waiting rather than applying. */
export const isAfterLastDay = (entry: Entry, lastDay: string | null): boolean =>
  lastDay !== null && entry.effectiveDate > lastDay

export interface EntryBounds {
  today: string
  /** The worker's first day, else the engagement's start. */
  firstDay: string | null
  /** See {@link effectiveLastDay}. */
  lastDay: string | null
}

export interface EntryProblem {
  field: 'effectiveDate' | 'hoursPerDay'
  message: string
}

/** What is wrong with a change being added, worded as the service words it once the worker prefix is cut. */
export const entryProblems = (entry: Pick<Entry, 'effectiveDate' | 'hoursPerDay'>, bounds: EntryBounds): EntryProblem[] => {
  const change = `The budgeted hours change on ${entry.effectiveDate}`
  const problems: EntryProblem[] = []

  if (entry.hoursPerDay < 0 || entry.hoursPerDay > MAX_BUDGETED_HOURS_PER_DAY) {
    problems.push({ field: 'hoursPerDay', message: `${change} must be between 0 and ${MAX_BUDGETED_HOURS_PER_DAY} hours per day.` })
  }

  // A change dated today waits for the next nightly run, which a direct edit to the hours does not.
  if (entry.effectiveDate < bounds.today) {
    problems.push({ field: 'effectiveDate', message: `${change} cannot take effect in the past.` })
  } else if (entry.effectiveDate === bounds.today) {
    problems.push({
      field: 'effectiveDate',
      message: `${change} must take effect after today. To change the hours from today, edit Budgeted Hours per Day directly.`,
    })
  } else if (
    (bounds.firstDay !== null && entry.effectiveDate < bounds.firstDay) ||
    (bounds.lastDay !== null && entry.effectiveDate > bounds.lastDay)
  ) {
    problems.push({ field: 'effectiveDate', message: `${change} must fall within the worker's dates on this engagement.` })
  }

  return problems
}

/**
 * Dates more than one uncancelled change takes effect on, where at least one is still pending. Two applied
 * entries on one date are history, as when a change applies and the hours are then edited directly the same
 * day, and the later one is what took effect.
 */
export const duplicateDates = (entries: Entry[] | null | undefined): string[] => {
  const byDate = new Map<string, Entry[]>()
  for (const entry of entries ?? []) {
    if (!entry.cancelledDate) {
      byDate.set(entry.effectiveDate, [...(byDate.get(entry.effectiveDate) ?? []), entry])
    }
  }
  return [...byDate]
    .filter(([, group]) => group.length > 1 && group.some(isPending))
    .map(([date]) => date)
    .sort()
}

/**
 * Cancels the entry at `index`. One the service already holds keeps its place and is stamped, since the
 * audit diff matches entries by position; one not yet saved is dropped, as there is nothing to record.
 */
export const cancelEntry = (entries: Entry[], index: number, today: string): Entry[] =>
  entries.flatMap((entry, position) => {
    if (position !== index) {
      return [entry]
    }
    return entry.id ? [{ ...entry, cancelledDate: today }] : []
  })

/**
 * The pending changes a copied worker takes onto the new engagement, as new entries. What was applied or
 * cancelled is the source engagement's history, and the copy's own budgeted hours already reflect it.
 */
export const copyPendingEntries = (entries: Entry[] | null | undefined): Entry[] =>
  pendingEntries(entries).map(({ effectiveDate, hoursPerDay }) => ({
    id: null,
    effectiveDate,
    hoursPerDay,
    appliedDate: null,
    cancelledDate: null,
  }))

export interface LastDayOfWorkEffects {
  /** Pending changes after a moved-earlier last day. They stay pending and apply only if the day moves back out. */
  notTakingEffect: Entry[]
  /** Pending changes a moved-later last day brings back in that are dated today or earlier, so apply on the next run. */
  nowDue: Entry[]
}

/**
 * What moving a worker's last day does to their pending changes, both days as {@link effectiveLastDay}
 * reads them (null when open-ended). Nothing is cancelled either way; this says what the reader should know
 * before saving.
 */
export const lastDayOfWorkEffects = (
  entries: Entry[] | null | undefined,
  previousLastDay: string | null,
  nextLastDay: string | null,
  today: string
): LastDayOfWorkEffects => {
  const pending = pendingEntries(entries)

  const movedEarlier = nextLastDay !== null && (previousLastDay === null || nextLastDay < previousLastDay)
  const movedLater = previousLastDay !== null && (nextLastDay === null || nextLastDay > previousLastDay)

  return {
    notTakingEffect: movedEarlier
      ? pending.filter(entry => entry.effectiveDate > nextLastDay!)
      : [],
    nowDue: movedLater
      ? pending.filter(entry =>
          entry.effectiveDate > previousLastDay! &&
          entry.effectiveDate <= today &&
          (nextLastDay === null || entry.effectiveDate <= nextLastDay))
      : [],
  }
}
